import type {FC} from 'react';
import {useCallback, useContext, useEffect} from 'react';

import type {Props} from './types';

import {ThemeContext} from '../ThemeProvider';

const darkSchemeMediaQuery =
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-color-scheme: dark)');

export const ThemeController: FC<Props> = ({theme = 'auto'}) => {
  const {setTheme: switchTheme} = useContext(ThemeContext);

  const onColorSchemeChange = useCallback(
    (e: MediaQueryListEvent) => {
      if (theme === 'auto') {
        switchTheme(e.matches ? 'dark' : 'light');
      } else {
        switchTheme(theme);
      }
    },
    [theme, switchTheme],
  );

  useEffect(() => {
    if (theme === 'auto') {
      switchTheme(
        darkSchemeMediaQuery && darkSchemeMediaQuery.matches ? 'dark' : 'light',
      );
    } else {
      switchTheme(theme);
    }
  }, [theme, switchTheme]);

  useEffect(() => {
    if (darkSchemeMediaQuery) {
      if (darkSchemeMediaQuery.addEventListener) {
        darkSchemeMediaQuery.addEventListener('change', onColorSchemeChange);
      } else {
        darkSchemeMediaQuery.addListener(onColorSchemeChange);
      }
    }

    return () => {
      if (darkSchemeMediaQuery) {
        if (darkSchemeMediaQuery.removeEventListener) {
          darkSchemeMediaQuery.removeEventListener(
            'change',
            onColorSchemeChange,
          );
        } else {
          darkSchemeMediaQuery.removeListener(onColorSchemeChange);
        }
      }
    };
  }, [onColorSchemeChange]);

  return null;
};
